import { AvField, AvForm } from "availity-reactstrap-validation";
import { withNamespaces } from "react-i18next";
import { Col, Input, Label, Row, Spinner } from "reactstrap";
import CreatableSelect from "react-select/creatable";
import { Accordion } from "react-bootstrap";
import { useEffect, useState } from "react";
import AssetDeatilsImage from "src/modules/data-protection/assets/images/basic-info-gdpr.svg";
import { useMutation, useQuery } from "@tanstack/react-query";
import DataProtectionSoftwareService from "src/modules/data-protection/apis/DataProtectionSoftwareService";
import debounce from "lodash/debounce";
import { toast } from "react-toastify";
import { RiCheckLine, RiCloseCircleLine } from "react-icons/ri";

const ASSET_KIND_LABELS = {
    AI_MODEL: "AI Model",
    APPLICATION: "Application",
};

const useAutoResetMutation = (mutationFn, resetTime = 3000) => {
    const mutation = useMutation(mutationFn);

    useEffect(() => {
        let timer;
        if (mutation.isSuccess || mutation.isError) {
            timer = setTimeout(() => {
                mutation.reset();
            }, resetTime);
        }
        return () => clearTimeout(timer);
    }, [mutation.isSuccess, resetTime]);

    return mutation;
};

const SecondRow = ({
    // General
    t,
    assetId,
    isOwnDevelopment = false,
    handleFetchSoftwareDetailsQuery,
    // Provider
    selectedProvider,
    setSelectedProvider,
    // Asset Type
    selectedAssetType,
    setSelectedAssetType,
    // Version
    version,
    // Service
    service,
    // Modules
    selectedModules,

    notice,
    setNotice,
    intendedPurpose,
    setIntendedPurpose,

    countries,
    selectedHostingLocation,
    setSelectedHostingLocation,
    selectedNoneEUHostingLocation,
    setSelectedNoneEUHostingLocation
}) => {
    const [activeAccordion, setActiveAccordion] = useState(null);
    const [providers, setProviders] = useState([]);
    const [assetTypes, setAssetTypes] = useState([]);
    const [moduleOptions, setModuleOptions] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const softwareDetails = handleFetchSoftwareDetailsQuery?.data;
    const assetKind = softwareDetails?.asset_kind || "";
    const isAiSystem = !!softwareDetails?.is_ai_system;
    const documentationUrl = softwareDetails?.url || "";

    const onUploadProgress = (e, file, setUploadedFiles) => {
        const { total, loaded, lengthComputable } = e;

        if (lengthComputable) {
            const uploadedPercent = (loaded / total) * 100;

            setUploadedFiles((uploadedFiles) => {
                const index = uploadedFiles.findIndex(
                    (f) => f.preview === file.preview
                );

                if (index > -1) {
                    uploadedFiles[index].uploadedPercent = uploadedPercent;

                    return [...uploadedFiles];
                }

                return [...uploadedFiles];
            });
        }
    };

    // Modules
    // Fetch
    const handelFetchModulesListQuery = useQuery({
        queryKey: ["data-protection-modules-list", moduleOptions],
        queryFn: async () => {
            const service = DataProtectionSoftwareService.getInstance();

            const response = await service.modules();
            const data =
                response && response.length
                    ? response.map((r) => ({
                        label: r.name,
                        value: r.id,
                    }))
                    : [];
            setModuleOptions(data);
            return data;
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching asset types."), {
                type: "error",
            });
        },
    });

    // Add to mdules
    const handleCreateModuleMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.addModule(payload);
        },
        onSuccess: () => {
            handleFetchSoftwareDetailsQuery.refetch();
            handelFetchModulesListQuery.refetch();
            toast(t("New module added successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while adding new module."), {
                type: "error",
            });
        },
    });

    const onCreateModuleOption = (label) => {
        handleCreateModuleMutation.mutate({
            software: Number(assetId),
            module: label,
        });
    };
    // Remove Module
    const handleRemoveModuleMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.removeModule(payload);
        },
        onSuccess: () => {
            handleFetchSoftwareDetailsQuery.refetch();
            handelFetchModulesListQuery.refetch();
            toast(t("New module removed successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while removing new module."), {
                type: "error",
            });
        },
    });

    const handleModuleChange = (selectedOptions, actionMeta) => {
        if (actionMeta.action === "remove-value") {
            const { removedValue } = actionMeta;
            handleRemoveModuleMutation.mutate({
                software: Number(assetId),
                module: Number(removedValue?.value),
            });
        } else {
            const { option } = actionMeta;
            handleCreateModuleMutation.mutate({
                software: Number(assetId),
                module: option?.label,
            });
        }
    };

    const handleChangeNoticeMutation = useAutoResetMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.changeNotice(payload);
        }
    });

    const debouncedHandleNoticeChange = debounce((event) => {
        const newData = event.target.value;
        handleChangeNoticeMutation.mutate({
            software: Number(assetId),
            notice: newData,
        });
    }, 900);

    const handleChangeIntendedPurposeMutation = useAutoResetMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.changeIntendedPurpose(payload);
        },
    });

    const debouncedHandleIntendedPurposeChange = debounce((event) => {
        const newData = event.target.value;
        handleChangeIntendedPurposeMutation.mutate({
            software: Number(assetId),
            intendedPurpose: newData,
        });
    }, 900);

    /**
     * Hosting locations
     */

    const handleCreateEULocationMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.addEULocation(payload);
        },
        onSuccess: () => {
            toast(t("New location added successfully."), {
                type: "success"
            });
        },
        onError: () => {
            toast(t("An error occurred while adding the new location."), {
                type: "error"
            });
        }
    });

    const handleRemoveEULocationMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.removeEULocation(payload);
        },
        onSuccess: () => {
            toast(t("New location added successfully."), {
                type: "success"
            });
        },
        onError: () => {
            toast(t("An error occurred while adding the new location."), {
                type: "error"
            });
        }
    });

    const handleEULocationChange = (selectedOptions, actionMeta) => {
        if (actionMeta.action === "remove-value") {
            const { removedValue } = actionMeta;
            handleRemoveEULocationMutation.mutate({
                software: Number(assetId),
                country: Number(removedValue?.value),
            }, {
                onSuccess: () => {
                    setSelectedHostingLocation((currentState) => {
                        return [
                            ...currentState
                        ].filter((loc) => loc.value !== removedValue.value)
                    })
                }
            });
        } else {
            const { option } = actionMeta;
            handleCreateEULocationMutation.mutate({
                software: Number(assetId),
                country: option?.value,
            }, {
                onSuccess: () => {
                    setSelectedHostingLocation((currentState) => {
                        return [
                            ...currentState,
                            {...option}
                        ]
                    })
                }
            });
        }
    };

    /**
     * None EU hosting locations
     */
    
    const handleCreateNoneEULocationMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.addNoneEULocation(payload);
        },
        onSuccess: () => {
            toast(t("New location added successfully."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while adding new location."), {
                type: "error",
            });
        },
    });

    const handleRemoveNoneEULocationMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.removeNoneEULocation(payload);
        },
        onSuccess: () => {
            toast(t("Location removed successfully."), {
                type: "success"
            });
        },
        onError: () => {
            toast(t("An error occurred while removing location."), {
                type: "error"
            });
        },
    });

    const changeTypeMutation = useMutation({
        mutationFn: async (payload) => {
            const service = DataProtectionSoftwareService.getInstance();
            return await service.changeType(payload);
        },
        onSuccess: () => {
            toast(t("Asset type successfully changed."), {
                type: "success",
            });
        },
        onError: () => {
            toast(t("An error occurred while updating asset type."), {
                type: "error",
            });
        }
    });

    const handleFetchAssetTypesListQuery = useQuery({
        queryKey: ["data-protection-asset-types-list"],
        queryFn: async () => {
            const service = DataProtectionSoftwareService.getInstance();

            return await service.types(1000, 1);
        },
        cacheTime: 0,
        refetchOnWindowFocus: false,
        onError: (error) => {
            if (process.env.NODE_ENV === 'development') console.error(error);

            toast(t("An error occurred while fetching asset types."), {
                type: "error",
            });
        },
    });

    const handleNoneEULocationChange = (selectedOptions, actionMeta) => {
        if (actionMeta.action === "remove-value") {
            const { removedValue } = actionMeta;
            handleRemoveNoneEULocationMutation.mutate({
                software: Number(assetId),
                country: Number(removedValue?.value),
            }, {
                onSuccess: () => {
                    setSelectedNoneEUHostingLocation((currentState) => {
                        return [
                            ...currentState
                        ].filter((loc) => loc.value !== removedValue.value)
                    })
                }
            });
        } else {
            const { option } = actionMeta;
            handleCreateNoneEULocationMutation.mutate({
                software: Number(assetId),
                country: option?.value,
            }, {
                onSuccess: () => {
                    setSelectedNoneEUHostingLocation((currentState) => {
                        return [
                            ...currentState,
                            {...option}
                        ]
                    })
                }
            });
        }
    };

    useEffect(() => {
        if (handelFetchModulesListQuery.data) {
            const gotModules = handelFetchModulesListQuery.data;
            if (gotModules && Array.isArray(gotModules)) {
                setAssetTypes(
                    gotModules.map((gotModule) => {
                        return {
                            label: gotModule,
                            value: gotModule,
                        };
                    })
                );
            }
        }
    }, [handelFetchModulesListQuery.data]);

    return (
        <Accordion className="mt-4" defaultActiveKey={activeAccordion}>
            <Accordion.Item
                onClick={() => setActiveAccordion("asset-details")}
                className={activeAccordion === "asset-details" ? "active" : ""}
                eventKey={"asset-details"}
            >
                <Accordion.Header>
                    <img src={AssetDeatilsImage} alt="" />
                    <span>{t("Asset Details")}</span>
                </Accordion.Header>

                <Accordion.Body>
                    <Row>
                        <Col sm="12" md='4' className='mb-4'>
                            <Label className="form-label">{t("Software Name")}:</Label>
                            <Input
                                name="softwareName"
                                type="text"
                                className="form-control disabled"
                                value={service}
                                id="softwareName"
                                disabled
                            />
                        </Col>

                        {!isOwnDevelopment && (
                            <Col sm="12" md="4" className="mb-4">
                                <Label className="form-label">{t("Service Provider")}:</Label>
                                <Input
                                    name="serviceProvider"
                                    type="text"
                                    className="form-control disabled"
                                    value={t(selectedProvider?.label)}
                                    id="serviceProvider"
                                    disabled
                                />
                            </Col>
                        )}

                        {!isOwnDevelopment && (
                            <Col sm="12" md="4" className="mb-4">
                                <Label className="form-label">{t("Type")}:</Label>
                                <Input
                                    name="asset-type"
                                    type="select"
                                    className="form-control"
                                    value={selectedAssetType || ""}
                                    id="asset-type"
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        changeTypeMutation.mutate({
                                            software: assetId,
                                            type: e.target.value
                                        }, {
                                            onSuccess: () => {
                                                setSelectedAssetType(val);
                                            }
                                        });
                                    }}
                                    disabled={changeTypeMutation.isLoading}
                                >
                                    {handleFetchAssetTypesListQuery?.data?.map((type) => {
                                        return (
                                            <option key={type} value={type}>
                                                {type}
                                            </option>
                                        );
                                    })}
                                </Input>
                                {changeTypeMutation.isLoading && (
                                    <>
                                        <Spinner size="sm" color="primary" />
                                        <small className="text-info ms-1">
                                            {t("Changing")}...
                                        </small>
                                    </>
                                )}
                            </Col>
                        )}

                        {isOwnDevelopment && (
                            <Col sm="12" md="4" className="mb-4">
                                <Label className="form-label">{t("Asset Kind")}:</Label>
                                <Input
                                    name="asset-kind"
                                    type="text"
                                    className="form-control disabled"
                                    value={t(ASSET_KIND_LABELS[assetKind] || assetKind)}
                                    id="asset-kind"
                                    disabled
                                />
                            </Col>
                        )}

                        {isOwnDevelopment && assetKind === "AI_MODEL" && (
                            <Col sm="12" md="4" className="mb-4">
                                <Label className="form-label">{t("AI System")}:</Label>
                                <Input
                                    name="is-ai-system"
                                    type="text"
                                    className="form-control disabled"
                                    value={isAiSystem ? t("Yes") : t("No")}
                                    id="is-ai-system"
                                    disabled
                                />
                            </Col>
                        )}

                        {isOwnDevelopment && version && (
                            <Col sm="12" md="4" className="mb-4">
                                <Label className="form-label">{t("Version")}:</Label>
                                <Input
                                    name="version"
                                    type="text"
                                    className="form-control disabled"
                                    value={version}
                                    id="version"
                                    disabled
                                />
                            </Col>
                        )}

                        {isOwnDevelopment && documentationUrl && (
                            <Col sm="12" md="4" className="mb-4">
                                <Label className="form-label">{t("Documentation URL")}:</Label>
                                <Input
                                    name="documentation-url"
                                    type="text"
                                    className="form-control disabled"
                                    value={documentationUrl}
                                    id="documentation-url"
                                    disabled
                                />
                            </Col>
                        )}

                        <Col sm='12' md='6' className='mb-4'>
                            <Label className="form-label">
                                {t("Hosting Location")}:
                            </Label>
                            <CreatableSelect
                                className="mt-1 text-capitalize"
                                placeholder={t("Select") + "..."}
                                options={countries}
                                menuPortalTarget={document.body}
                                isMulti
                                value={selectedHostingLocation}
                                onChange={handleEULocationChange}
                                isClearable={false}
                            />
                        </Col>

                        {!isOwnDevelopment && (
                            <Col sm="12" md="6" className="mb-4">
                                <Label className="form-label">
                                    {t("None EU Hosting Location")}:
                                </Label>
                                <CreatableSelect
                                    className="mt-1 text-capitalize"
                                    placeholder={t("Select") + "..."}
                                    options={countries}
                                    menuPortalTarget={document.body}
                                    isMulti
                                    value={selectedNoneEUHostingLocation}
                                    onChange={handleNoneEULocationChange}
                                    isClearable={false}
                                />
                            </Col>
                        )}

                        <Col sm="12" md='6' className='mb-4'>
                            <Label className="form-label">{t("Software Module")}:</Label>
                            <CreatableSelect
                                className="mt-1 text-capitalize"
                                placeholder={t("Select") + "..."}
                                options={moduleOptions}
                                menuPortalTarget={document.body}
                                isMulti
                                onCreateOption={onCreateModuleOption}
                                onChange={handleModuleChange}
                                value={selectedModules}
                                isClearable={false}
                            />
                        </Col>

                        {!isOwnDevelopment && (
                            <Col sm="12">
                                <Label className="form-label">{t("Notice")}:</Label>
                                <AvForm>
                                    <AvField
                                        name="notice"
                                        errorMessage={t("This field cannot be blank")}
                                        type="textarea"
                                        value={notice}
                                        onChange={debouncedHandleNoticeChange}
                                    />

                                    {handleChangeNoticeMutation.isLoading && (
                                        <>
                                            <Spinner size="sm" color="info" />
                                            <small className="text-info ms-1">
                                                {t("Saving")}...
                                            </small>
                                        </>
                                    )}

                                    {handleChangeNoticeMutation.isError && (
                                        <small className="text-danger">
                                            <RiCloseCircleLine size="16" className="me-1" />
                                            {t("Unable to save changes")}
                                        </small>
                                    )}

                                    {handleChangeNoticeMutation.isSuccess && (
                                        <small className="text-success">
                                            <RiCheckLine size="16" className="me-1" />
                                            {t("Changes saved")}
                                        </small>
                                    )}
                                </AvForm>
                            </Col>
                        )}

                        {isOwnDevelopment && (
                            <Col sm="12">
                                <Label className="form-label">{t("Intended Purpose")}:</Label>
                                <AvForm>
                                    <AvField
                                        name="intended-purpose"
                                        errorMessage={t("This field cannot be blank")}
                                        type="textarea"
                                        value={intendedPurpose}
                                        onChange={debouncedHandleIntendedPurposeChange}
                                    />

                                    {handleChangeIntendedPurposeMutation.isLoading && (
                                        <>
                                            <Spinner size="sm" color="info" />
                                            <small className="text-info ms-1">
                                                {t("Saving")}...
                                            </small>
                                        </>
                                    )}

                                    {handleChangeIntendedPurposeMutation.isError && (
                                        <small className="text-danger">
                                            <RiCloseCircleLine size="16" className="me-1" />
                                            {t("Unable to save changes")}
                                        </small>
                                    )}

                                    {handleChangeIntendedPurposeMutation.isSuccess && (
                                        <small className="text-success">
                                            <RiCheckLine size="16" className="me-1" />
                                            {t("Changes saved")}
                                        </small>
                                    )}
                                </AvForm>
                            </Col>
                        )}
                    </Row>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

export default withNamespaces()(SecondRow);
